'use client';
import { createChatSession } from '@/app/services/chat';
import SearchInputCard, {
  type SummaryStyle,
} from '@/components/search/search-input-card';
import { insertSidebarSearchHistory } from '@/components/sidebar-history/sidebar-history-cache';
import { SearchModel, searchModels } from '@/config/site';
import { useMaintenance } from '@/hooks/use-maintenance';
import { useNewsEventTracker } from '@/hooks/use-news-event-tracker';
import { usePointInfo } from '@/hooks/use-point-info';
import { useAuth } from '@/providers/auth-provider';
import { useQueryClient } from '@tanstack/react-query';
import dynamic from 'next/dynamic';
import { useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useState, useTransition } from 'react';
import { toast } from 'sonner';

const InsufficientCreditsDialog = dynamic(
  () => import('./insufficient-credits-dialog'),
  { ssr: false },
);

const searchModalPointMap: Record<SearchModel, number> = {
  pure_search: 5,
  regular: 15,
  flagship: 30,
  deep: 45,
};

const normalizeStoredStyle = (value: string | null): SummaryStyle =>
  value === 'simple' || value === 'popular_science' ? 'simple' : 'academic';

interface SearchTextareaProps {
  value?: string;
  onValueChange?: (value: string) => void;
}

const SearchTextarea = ({
  value: controlledValue,
  onValueChange,
}: SearchTextareaProps = {}) => {
  const searchParams = useSearchParams();
  const q = searchParams.get('q');
  const [loading, setLoading] = useState(false);
  const [currentModel, setCurrentModel] = useState<SearchModel>(() => {
    if (typeof window === 'undefined') return 'deep';
    const savedModel = localStorage.getItem(
      'search-currentModel',
    ) as SearchModel | null;
    return savedModel && searchModels.some((item) => item.value === savedModel)
      ? savedModel
      : 'deep';
  });
  const [internalValue, setInternalValue] = useState(q || '');
  const [isOpen, setIsOpen] = useState(false);
  const [isPending, startTransition] = useTransition();
  const [style, setStyle] = useState<SummaryStyle>(() => {
    if (typeof window === 'undefined') return 'academic';
    return normalizeStoredStyle(localStorage.getItem('search-style'));
  });

  const { info, getInfo } = usePointInfo();
  const { user, triggerLogin } = useAuth();
  const { trackSearch } = useNewsEventTracker();
  const router = useRouter();
  const { fetchMaintenanceStatus, showMaintenance } = useMaintenance();
  const queryClient = useQueryClient();
  const value = controlledValue ?? internalValue;

  const handleValueChange = (nextValue: string) => {
    if (onValueChange) {
      onValueChange(nextValue);
      return;
    }

    setInternalValue(nextValue);
  };

  // 保存当前模型到 localStorage
  useEffect(() => {
    try {
      localStorage.setItem('search-currentModel', currentModel);
    } catch (error) {
      console.warn('Failed to save currentModel to localStorage:', error);
    }
  }, [currentModel]);

  // 保存当前风格到 localStorage
  useEffect(() => {
    try {
      localStorage.setItem('search-style', style);
    } catch (error) {
      console.warn('Failed to save style to localStorage:', error);
    }
  }, [style]);

  const onEnter = async () => {
    setLoading(true);
    if (!value.trim()) {
      setLoading(false);
      return;
    }
    const info = await getInfo();
    if (!info) {
      setLoading(false);
      return;
    }
    if (
      info.available <
      info.total_running_task_estimated_point_deep_research +
        searchModalPointMap[currentModel]
    ) {
      setIsOpen(true);
      setLoading(false);
      return;
    }

    const maintenanceStatus = await fetchMaintenanceStatus('doc_search');

    if (maintenanceStatus && maintenanceStatus.is_maintenance) {
      showMaintenance(maintenanceStatus.title, maintenanceStatus.content);
      setLoading(false);
      return;
    }

    try {
      trackSearch(value);
      const { data } = await createChatSession({
        query: value,
        search_mode: currentModel,
        summary_style: style,
        metadata: { source: 'home_search' },
      });
      const now = new Date().toISOString();
      insertSidebarSearchHistory(queryClient, {
        session_id: data.session_id,
        user_id: '',
        title: data.title || value.trim(),
        title_source: 'auto',
        created_at: now,
        updated_at: now,
      });
      startTransition(() => {
        router.push(`/search?sid=${data.session_id}`);
      });
    } catch (e) {
      const err = e as Error & { code?: number };
      if (err.code === 8507) {
        toast.error(err.message, {
          position: 'top-center',
        });
      }
      console.error(e);
    }
    setLoading(false);
  };

  const onSearch = async () => {
    if (!user && triggerLogin) {
      triggerLogin(() => {
        onEnter();
      });
      return;
    }
    await onEnter();
  };

  const disabled = loading || !value?.trim() || isPending;

  return (
    <div className="content-center relative max-w-196 mx-auto w-full">
      <div className="relative z-10 flex w-full flex-col rounded-xl">
        <SearchInputCard
          value={value}
          onChange={handleValueChange}
          onSubmit={onSearch}
          mode={currentModel}
          onModeChange={setCurrentModel}
          style={style}
          onStyleChange={setStyle}
          placeholder="输入你感兴趣的任何学术领域或文献检索需求"
          disabled={disabled}
          loading={loading || isPending}
        />

        {isOpen && (
          <InsufficientCreditsDialog
            open={isOpen}
            onOpenChange={setIsOpen}
            pointCost={searchModalPointMap[currentModel]}
            available={info.available}
            taskCount={info.task_count_deep_research}
            runningTaskPoints={
              info.total_running_task_estimated_point_deep_research
            }
          />
        )}
      </div>
    </div>
  );
};

export default SearchTextarea;
